import type { MessageAppearanceRow, ParentBoardRow } from './types'

export const makeMatchKey = (parentId: string, boardId: string) => `${parentId}:${boardId}`

export const lowerBoundDescByCreated = (appearances: MessageAppearanceRow[], createdAt: number) => {
  let lo = 0
  let hi = appearances.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (appearances[mid].created > createdAt) {
      lo = mid + 1
    } else {
      hi = mid
    }
  }
  return lo
}

export const findMatchedAppearance = (
  appearances: MessageAppearanceRow[],
  parentCreated: number,
  parentAttribution: string,
  matchWindowMs: number
) => {
  if (appearances.length === 0) return null

  const maxCreated = parentCreated + matchWindowMs
  const rangeStart = lowerBoundDescByCreated(appearances, maxCreated)
  const rangeEnd = lowerBoundDescByCreated(appearances, parentCreated - 1)
  if (rangeStart >= rangeEnd) return null

  const pickClosestInWindow = (requireAttributionMatch: boolean) => {
    let best: MessageAppearanceRow | null = null
    let bestDistance = Number.POSITIVE_INFINITY

    for (let i = rangeStart; i < rangeEnd; i += 1) {
      const appearance = appearances[i]
      if (requireAttributionMatch && parentAttribution && appearance.attribution !== parentAttribution) {
        continue
      }
      const distance = appearance.created - parentCreated
      if (distance < bestDistance) {
        best = appearance
        bestDistance = distance
      }
    }

    return best
  }

  return pickClosestInWindow(true) ?? pickClosestInWindow(false)
}

export const sortParentBoardRows = (
  rows: ParentBoardRow[],
  parentCreated: number,
  mergedAckByAppearanceId: Map<string, number>,
  ackFetchFailed: boolean
) => {
  return rows
    .map((row, index) => ({ row, index }))
    .sort((leftEntry, rightEntry) => {
      if (ackFetchFailed) {
        return leftEntry.index - rightEntry.index
      }

      const leftMatched = leftEntry.row.matchedAppearance
      const rightMatched = rightEntry.row.matchedAppearance
      const leftAckTimestamp = leftMatched ? mergedAckByAppearanceId.get(leftMatched.id) : undefined
      const rightAckTimestamp = rightMatched ? mergedAckByAppearanceId.get(rightMatched.id) : undefined

      const leftRank = !leftMatched ? 2 : leftAckTimestamp == null ? 1 : 0
      const rightRank = !rightMatched ? 2 : rightAckTimestamp == null ? 1 : 0
      if (leftRank !== rightRank) return leftRank - rightRank

      if (leftRank === 0 && rightRank === 0) {
        const leftDelay = Math.max(0, Math.round((leftAckTimestamp || 0) - parentCreated))
        const rightDelay = Math.max(0, Math.round((rightAckTimestamp || 0) - parentCreated))
        if (leftDelay !== rightDelay) return leftDelay - rightDelay
      }

      return leftEntry.index - rightEntry.index
    })
    .map(entry => entry.row)
}
